import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  ShoppingBag, 
  Check, 
  Maximize2, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  MessageCircle, 
  Sparkles, 
  RefreshCw, 
  ArrowRight,
  Share2,
  Heart
} from 'lucide-react';
import CollectionHeader from '../components/CollectionHeader';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { trackEvent } from '../lib/analytics';
import initialPhotosData from '../data/collectionPhotos.json';

const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyjY4WAfcctIEUn9niDNRLkcMpByfGYJW-UBPhIs9HzK_W_yUOQP9O_XTZzaufmmPxp0A/exec';
const INITIAL_BATCH = 16;
const BATCH_SIZE = 12;

const CollectionPage = () => {
  const { cart, cartCount, isInCart, toggleCart, phoneNumber } = useCart();
  const [searchParams, setSearchParams] = useSearchParams();

  // Photos state: initialize from bundled JSON, check localStorage for cached updates
  const [photos, setPhotos] = useState(() => {
    try {
      const cached = localStorage.getItem('mbk_synced_photos');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      console.error('Error reading cached photos', e);
    }
    return initialPhotosData.photos || [];
  });

  const [isSyncing, setIsSyncing] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_BATCH);
  const [activeLightboxIndex, setActiveLightboxIndex] = useState(null);
  const observerRef = useRef(null);

  // Check if URL has shared item IDs
  const sharedItemIds = useMemo(() => {
    const itemsParam = searchParams.get('items') || searchParams.get('cart');
    if (!itemsParam) return null;
    return itemsParam.split(',').map((id) => id.trim()).filter(Boolean);
  }, [searchParams]);

  // Filtered or all photos
  const displayedPhotos = useMemo(() => {
    if (sharedItemIds && sharedItemIds.length > 0) {
      const filtered = photos.filter((p) => sharedItemIds.includes(p.id));
      if (filtered.length > 0) return filtered;
    }
    return photos;
  }, [photos, sharedItemIds]);

  // Current slice for infinite scroll
  const visiblePhotos = useMemo(() => {
    return displayedPhotos.slice(0, visibleCount);
  }, [displayedPhotos, visibleCount]);

  const hasMore = visibleCount < displayedPhotos.length;

  // Background auto-sync with Google Apps Script
  useEffect(() => {
    let isMounted = true;
    const fetchLatest = async () => {
      try {
        setIsSyncing(true);
        const res = await fetch(SCRIPT_URL);
        if (!res.ok) throw new Error('Failed to fetch from Google Apps Script');
        const data = await res.json();
        if (data && Array.isArray(data.photos) && data.photos.length > 0) {
          if (isMounted) {
            setPhotos(data.photos);
            localStorage.setItem('mbk_synced_photos', JSON.stringify(data.photos));
          }
        }
      } catch (err) {
        console.warn('Background sync note: Using bundled photos list', err);
      } finally {
        if (isMounted) setIsSyncing(false);
      }
    };

    fetchLatest();
    return () => {
      isMounted = false;
    };
  }, []);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => Math.min(prev + BATCH_SIZE, displayedPhotos.length));
        }
      },
      { threshold: 0.1, rootMargin: '300px' }
    );

    const currentObserverRef = observerRef.current;
    if (currentObserverRef) {
      observer.observe(currentObserverRef);
    }

    return () => {
      if (currentObserverRef) observer.unobserve(currentObserverRef);
    };
  }, [hasMore, displayedPhotos.length]);

  // Track page view
  useEffect(() => {
    trackEvent('collection_page_view', {
      total_items: displayedPhotos.length,
      has_shared_filter: !!(sharedItemIds && sharedItemIds.length > 0),
    });
  }, [displayedPhotos.length, sharedItemIds]);

  const openLightbox = (index) => {
    const photo = displayedPhotos[index];
    if (photo) {
      trackEvent('product_quick_view', {
        item_id: photo.id,
        item_code: `Item #${index + 1}`,
        item_name: photo.name,
        index: index + 1,
      });
    }
    setActiveLightboxIndex(index);
  };

  // Keyboard navigation for Lightbox
  const handleKeyDown = useCallback(
    (e) => {
      if (activeLightboxIndex === null) return;
      if (e.key === 'ArrowRight') {
        setActiveLightboxIndex((prev) => (prev + 1) % displayedPhotos.length);
      } else if (e.key === 'ArrowLeft') {
        setActiveLightboxIndex((prev) => (prev - 1 + displayedPhotos.length) % displayedPhotos.length);
      } else if (e.key === 'Escape') {
        setActiveLightboxIndex(null);
      }
    },
    [activeLightboxIndex, displayedPhotos.length]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const currentLightboxPhoto = activeLightboxIndex !== null ? displayedPhotos[activeLightboxIndex] : null;

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col font-sans">
      <CollectionHeader />

      {/* Hero Banner */}
      <section className="bg-gradient-to-b from-rose-100/60 via-stone-50 to-stone-50 pt-8 pb-6 sm:pb-8 border-b border-rose-100/50">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-rose-50 border border-rose-200/80 px-3.5 py-1 rounded-full text-xs sm:text-sm font-medium text-rose-700 mb-4 shadow-xs">
              <Sparkles className="w-3.5 h-3.5 text-rose-500 animate-spin-slow" />
              <span>Handcrafted & Curated Jewellery</span>
              <span className="text-stone-300">•</span>
              <span className="text-stone-500 font-normal">
                {photos.length} Pieces Available
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-serif font-bold text-stone-900 tracking-tight mb-3">
              The Jewellery & Style Collection
            </h1>

            <p className="text-stone-600 text-sm sm:text-base leading-relaxed mb-6">
              Explore our signature jewellery, floral sets, and festive accessories by Khushi Shukla.
              Tap any item to view high-res details, add pieces to your cart, and send them to Khushi on WhatsApp for custom pricing & availability!
            </p>

            {/* Step-by-Step Helper Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-white p-3.5 sm:p-4 rounded-xl border border-rose-100 shadow-sm text-left">
              <div className="flex items-center space-x-3 p-2 rounded-lg bg-stone-50/70">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-rose-100 text-rose-700 font-bold text-xs">
                  1
                </span>
                <span className="text-xs sm:text-sm text-stone-700 font-medium">
                  Browse & Add items to your cart
                </span>
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg bg-stone-50/70">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-rose-100 text-rose-700 font-bold text-xs">
                  2
                </span>
                <span className="text-xs sm:text-sm text-stone-700 font-medium">
                  Review your selected pieces
                </span>
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg bg-stone-50/70">
                <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#25D366]/20 text-[#128C7E] font-bold text-xs">
                  3
                </span>
                <span className="text-xs sm:text-sm text-stone-700 font-medium">
                  Chat with Khushi on WhatsApp for price
                </span>
              </div>
            </div>

            {/* Shared URL filter notice if active */}
            {sharedItemIds && sharedItemIds.length > 0 && (
              <div className="mt-4 p-3 bg-rose-50 border border-rose-300 rounded-lg flex items-center justify-between text-xs sm:text-sm">
                <span className="text-rose-800 font-medium">
                  Viewing {displayedPhotos.length} shared item(s) from a customer cart link.
                </span>
                <button
                  onClick={() => setSearchParams({})}
                  className="text-rose-700 underline font-semibold hover:text-rose-900 ml-3"
                >
                  View All {photos.length} Items
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Product Catalog Grid */}
      <main className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        <div className="flex items-center justify-between mb-6 pb-3 border-b border-stone-200">
          <div className="flex items-center space-x-2">
            <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-stone-500">
              Product Catalog
            </span>
            <Badge variant="secondary" className="bg-rose-50 text-rose-700 border-rose-200 text-xs">
              Showing {Math.min(visibleCount, displayedPhotos.length)} of {displayedPhotos.length}
            </Badge>
          </div>

          <div className="flex items-center space-x-3">
            {cartCount > 0 && (
              <Link 
                to="/cart"
                onClick={() => {
                  trackEvent('view_cart_click', { source: 'catalog_top_bar', items_in_cart: cartCount });
                }}
              >
                <Button
                  size="sm"
                  className="bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm shadow-sm"
                >
                  <ShoppingBag className="w-4 h-4 mr-1.5" />
                  View Cart ({cartCount})
                </Button>
              </Link>
            )}
            <span className="text-[11px] text-stone-600 flex items-center">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1.5 animate-pulse" />
              Live Stock
            </span>
          </div>
        </div>

        {/* Responsive Grid: 2 cols on mobile, 3 on tablet, 4 on desktop */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {visiblePhotos.map((photo, index) => {
            const inCart = isInCart(photo.id);
            const itemCode = `Item #${index + 1}`;
            const photoItem = { ...photo, title: itemCode };

            return (
              <div
                key={photo.id}
                className="group relative bg-white rounded-xl overflow-hidden border border-stone-200/80 shadow-xs hover:shadow-xl hover:border-rose-300 transition-all duration-300 flex flex-col"
              >
                {/* Image Container with 4:5 Aspect Ratio */}
                <div
                  className="relative aspect-[4/5] bg-stone-100 overflow-hidden cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={`https://lh3.googleusercontent.com/d/${photo.id}=w800`}
                    alt={photo.name || itemCode}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://lh3.googleusercontent.com/d/${photo.id}`;
                    }}
                  />

                  {/* Top Badges */}
                  <div className="absolute top-2.5 left-2.5 z-10">
                    <span className="px-2 py-0.5 bg-black/60 backdrop-blur-md text-white text-[11px] font-medium rounded-md shadow-xs">
                      {itemCode}
                    </span>
                  </div>

                  {/* Zoom indicator on hover */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 backdrop-blur-sm text-stone-800 p-2.5 rounded-full shadow-md transform scale-90 group-hover:scale-100">
                      <Maximize2 className="w-4 h-4" />
                    </div>
                  </div>

                  {/* Cart Status Ribbon if already in cart */}
                  {inCart && (
                    <div className="absolute top-2.5 right-2.5 z-10 bg-emerald-600 text-white px-2 py-0.5 rounded-md text-[11px] font-semibold flex items-center shadow-md">
                      <Check className="w-3 h-3 mr-1" />
                      In Cart
                    </div>
                  )}
                </div>

                {/* Card Content & Action Button */}
                <div className="p-3 sm:p-4 flex flex-col flex-1 justify-between bg-white">
                  <div className="mb-2">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-stone-900 text-xs sm:text-sm truncate">
                        {itemCode}
                      </h3>
                      <button
                        onClick={() => openLightbox(index)}
                        className="text-stone-400 hover:text-rose-500 text-xs flex items-center transition-colors"
                        title="View Full Size"
                      >
                        <Maximize2 className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-[11px] text-stone-500 truncate mt-0.5">
                      Jewellery & Styling piece
                    </p>
                  </div>

                  {/* Add to Cart / In Cart Button */}
                  <Button
                    onClick={() => toggleCart(photoItem)}
                    size="sm"
                    className={`w-full text-xs font-medium py-1.5 transition-all duration-200 ${
                      inCart
                        ? 'bg-stone-100 hover:bg-rose-50 text-rose-700 border border-rose-200'
                        : 'bg-stone-900 hover:bg-rose-600 text-white'
                    }`}
                  >
                    {inCart ? (
                      <>
                        <Check className="w-3.5 h-3.5 mr-1 text-emerald-600" />
                        <span>✓ Added (Remove)</span>
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-3.5 h-3.5 mr-1" />
                        <span>Add to Cart</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Infinite Scroll Sentinel / Loading Spinner */}
        {hasMore ? (
          <div ref={observerRef} className="py-12 flex justify-center items-center">
            <div className="flex items-center space-x-3 text-stone-500 text-sm">
              <RefreshCw className="w-5 h-5 animate-spin text-rose-500" />
              <span>Loading more pieces...</span>
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-stone-500 text-xs sm:text-sm">
            <div className="inline-flex items-center space-x-2 bg-stone-100 px-4 py-2 rounded-full">
              <span>✨ You've reached the end of the collection ({displayedPhotos.length} items)</span>
            </div>
            <div className="mt-4">
              <Link to="/cart">
                <Button className="bg-rose-600 hover:bg-rose-700 text-white text-xs sm:text-sm shadow-md">
                  <ShoppingBag className="w-4 h-4 mr-1.5" />
                  Proceed to Cart ({cartCount})
                </Button>
              </Link>
            </div>
          </div>
        )}
      </main>

      {/* Floating Sticky Cart Button (Bottom Right) */}
      {cartCount > 0 && (
        <div className="fixed bottom-6 right-6 z-40">
          <Link 
            to="/cart"
            onClick={() => {
              trackEvent('view_cart_click', { source: 'floating_pill', items_in_cart: cartCount });
            }}
          >
            <div className="bg-rose-600 hover:bg-rose-700 text-white px-4 sm:px-5 py-3 rounded-full shadow-2xl hover:shadow-[0_8px_30px_rgb(225,29,72,0.4)] flex items-center space-x-3 transition-all duration-300 transform hover:-translate-y-0.5 active:scale-95 group">
              <div className="relative">
                <ShoppingBag className="w-5 h-5" />
                <span className="absolute -top-1.5 -right-2 bg-white text-rose-700 font-extrabold text-[11px] w-4 h-4 rounded-full flex items-center justify-center shadow-xs">
                  {cartCount}
                </span>
              </div>
              <span className="text-sm font-semibold tracking-wide">
                View Cart ({cartCount})
              </span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </div>
          </Link>
        </div>
      )}

      {/* Full-Screen Lightbox Modal */}
      {currentLightboxPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6"
          onClick={() => setActiveLightboxIndex(null)}
        >
          {/* Top Bar with item info & Close */}
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between text-white z-20">
            <div className="flex items-center space-x-3">
              <span className="text-sm sm:text-base font-medium px-3 py-1 bg-white/20 backdrop-blur-md rounded-full">
                Item #{activeLightboxIndex + 1} of {displayedPhotos.length}
              </span>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveLightboxIndex(null);
              }}
              className="p-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation - Previous */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActiveLightboxIndex(
                (activeLightboxIndex - 1 + displayedPhotos.length) % displayedPhotos.length
              );
            }}
            className="absolute left-2 sm:left-6 p-2 sm:p-3 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-20"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>

          {/* High Resolution Image Container */}
          <div
            className="relative max-w-4xl max-h-[82vh] flex flex-col items-center justify-center z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={`https://lh3.googleusercontent.com/d/${currentLightboxPhoto.id}=w1600`}
              alt={currentLightboxPhoto.name || `Item #${activeLightboxIndex + 1}`}
              className="max-h-[72vh] max-w-full object-contain rounded-lg shadow-2xl"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://lh3.googleusercontent.com/d/${currentLightboxPhoto.id}`;
              }}
            />

            {/* Lightbox Bottom Controls */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
              <Button
                onClick={() => {
                  const item = { ...currentLightboxPhoto, title: `Item #${activeLightboxIndex + 1}` };
                  toggleCart(item);
                }}
                className={`text-xs sm:text-sm font-medium ${
                  isInCart(currentLightboxPhoto.id)
                    ? 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100'
                    : 'bg-rose-600 hover:bg-rose-700 text-white'
                }`}
              >
                {isInCart(currentLightboxPhoto.id) ? (
                  <>
                    <Check className="w-4 h-4 mr-1.5 text-emerald-600" />
                    In Cart (Remove)
                  </>
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4 mr-1.5" />
                    Add to Cart
                  </>
                )}
              </Button>

              <Link 
                to="/cart"
                onClick={() => {
                  trackEvent('view_cart_click', { source: 'lightbox_modal', items_in_cart: cartCount });
                }}
              >
                <Button className="bg-[#25D366] hover:bg-[#1EBE5D] text-white text-xs sm:text-sm shadow-md font-semibold">
                  <ShoppingBag className="w-4 h-4 mr-1.5" />
                  Go to Cart & Inquire ({cartCount})
                </Button>
              </Link>
            </div>
          </div>

          {/* Navigation - Next */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setActiveLightboxIndex((activeLightboxIndex + 1) % displayedPhotos.length);
            }}
            className="absolute right-2 sm:right-6 p-2 sm:p-3 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-20"
            aria-label="Next photo"
          >
            <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CollectionPage;
